import { useCreateGoal } from 'features/goals';
import { useUpdateTasks } from 'features/tasks';
import { useCallback } from 'react';
import { getTaskDocs } from 'shared/services/backend-api/get-tasks';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';
import { Task } from 'shared/types/task';

import { useUpdateLifeArea } from './use-update-life-area';

export const useTransformLifeAreaSectionToGoal = () => {
  const { submit: createGoal } = useCreateGoal();
  const { submit: updateTasks } = useUpdateTasks();
  const { submit: updateLifeArea } = useUpdateLifeArea();

  return useCallback(
    async (sectionId: ID, lifeArea: LifeArea) => {
      // 1. Get the section
      const section = lifeArea.sections?.find((s) => s.id === sectionId);
      if (!section) {
        return;
      }

      // 2. Get tasks of the section
      // Optimally we would just use the IDs in section.tasks, but fetching details ensures we are robust
      const allTasks = await getTaskDocs({ lifeAreas: [lifeArea.id] });
      const sectionTasks = section.tasks
        .map((taskId) => allTasks.find(({ id }) => id === taskId))
        .filter(Boolean) as Task[];

      // 3. Create a new Goal with the section name
      const newGoal = await createGoal(
        {
          name: section.name,
          lifeAreaId: lifeArea.id,
          taskSorting: sectionTasks.map((t) => t.id),
          taskCount: sectionTasks.length,
          completedTaskCount: sectionTasks.filter((t) => !!t.completedAt)
            .length,
        },
        { lifeArea },
      );

      if (!newGoal) {
        return;
      }

      // 4. Move tasks to the new Goal (update goalId and remove from life area section implicitly by changing goal)
      if (sectionTasks.length) {
        const updatedTasks = sectionTasks.map((task) => ({
          ...task,
          goalId: newGoal.id,
        }));
        await updateTasks(updatedTasks);
      }

      // 5. Remove the section from the Life Area
      // Since tasks are moved to a goal, they are no longer "direct children" of the life area in that section context
      const newSections = lifeArea.sections?.filter((s) => s.id !== sectionId);

      updateLifeArea({
        id: lifeArea.id,
        sections: newSections,
      });
    },
    [createGoal, updateTasks, updateLifeArea],
  );
};
